import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { useLearningPlanWork } from '../../api/learning-plans';
import { useSupportUnread } from '../../api/support';
import { useTrialPipeline } from '../../api/funnel';
import { useTutorApplications } from '../../api/tutor-applications';
import { Badge } from '../../components/Badge';
import {
  IconAlert,
  IconCalendar,
  IconChevron,
  IconLayers,
  IconMessage,
  IconRepeat,
  IconShield,
  IconSparkle,
} from '../../components/Icons';

interface Item {
  key: string;
  count: number | undefined;
  title: string;
  hint: string;
  to: string;
  icon: ReactNode;
  urgent?: boolean;
}

/**
 * What is waiting for administration right now, most time-sensitive first.
 * Each row opens the screen already filtered to those items. A source that
 * fails to load says so rather than reading as "nothing to do".
 */
export function TodayWorklist() {
  const plans = useLearningPlanWork();
  const { user, isImpersonating } = useAuth();
  // Same gate as the sidebar badge: the count is the admin's own inbox.
  const support = useSupportUnread(user?.role === 'admin' && !isImpersonating);
  const trials = useTrialPipeline();
  const applications = useTutorApplications({ status: 'submitted', limit: 1 });
  const work = plans.data;

  const items: Item[] = [
    {
      key: 'hold', count: work?.holdEnding, urgent: true, icon: <IconAlert className="size-5" />,
      title: 'مهلة حجز تنتهي خلال يوم', hint: 'ذكّر الأسرة بإتمام الدفع قبل تحرير المواعيد',
      to: '/learning-plans?status=hold_ending',
    },
    {
      key: 'lessons', count: work?.lessons, icon: <IconCalendar className="size-5" />,
      title: 'حصص تحتاج حسم حضور أو حصة بديلة', hint: 'سجّل ما حدث، أو رتّب الحصة المستحقة للأسرة',
      to: '/learning-plans?status=needs_lessons',
    },
    {
      key: 'changes', count: work?.changes, icon: <IconRepeat className="size-5" />,
      title: 'طلبات تعديل من الأسر', hint: 'ميعاد جديد أو معلّم آخر، بانتظار ردك',
      to: '/learning-plans?tab=changes',
    },
    {
      key: 'setup', count: work?.needsSetup, icon: <IconLayers className="size-5" />,
      title: 'خطط تحتاج معلّمًا وجدولًا', hint: 'عيّن المعلّم والمواعيد لتصل الأسرة إلى الدفع',
      to: '/learning-plans?status=needs_setup',
    },
    {
      key: 'support', count: support.data?.unreadCount, icon: <IconMessage className="size-5" />,
      title: 'رسائل دعم غير مقروءة', hint: 'أسئلة من الطلاب والمعلّمين',
      to: '/support',
    },
    {
      key: 'trials', count: trials.data?.byStage.awaiting_assignment, icon: <IconSparkle className="size-5" />,
      title: 'حصص تجريبية تحتاج معلّمًا', hint: 'حجوزات الباقات التجريبية',
      to: '/trials',
    },
    {
      key: 'applications', count: applications.data?.meta.total, icon: <IconShield className="size-5" />,
      title: 'طلبات انضمام معلّمين', hint: 'راجع الملف والمستندات ثم اقبل أو اطلب تعديلًا',
      to: '/tutor-applications',
    },
  ];
  const sources = [plans, support, trials, applications];
  const loading = sources.some((s) => s.isLoading);
  const failed = [
    plans.isError && 'الخطط التعليمية',
    support.isError && 'رسائل الدعم',
    trials.isError && 'الحصص التجريبية',
    applications.isError && 'طلبات المعلّمين',
  ].filter(Boolean);
  const due = items.filter((item) => (item.count ?? 0) > 0);
  const total = due.reduce((sum, item) => sum + (item.count ?? 0), 0);

  return (
    <section lang="ar" dir="rtl" aria-labelledby="worklist-title" className="panel mb-4 p-4">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="worklist-title" className="text-base font-semibold text-ink">مطلوب منك الآن</h2>
        {!loading && due.length > 0 && <span className="text-xs text-ink-3">الإجمالي {total} · الأكثر إلحاحًا أولًا</span>}
      </div>

      {loading && (
        <div className="space-y-2" aria-busy="true">
          <div className="skeleton h-14" />
          <div className="skeleton h-14" />
        </div>
      )}

      {!loading && due.length === 0 && failed.length === 0 && (
        <p className="rounded-lg border border-dashed border-rule p-4 text-center text-sm text-ink-2" role="status">
          لا يوجد ما يحتاج تصرّفك الآن.
        </p>
      )}

      {!loading && due.length > 0 && (
        <ul className="divide-y divide-rule overflow-hidden rounded-lg border border-rule">
          {due.map((item) => (
            <li key={item.key}>
              <Link
                to={item.to}
                data-worklist={item.key}
                className="flex items-center gap-3 px-3 py-3 transition-colors hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none"
              >
                <span className={item.urgent ? 'text-danger' : 'text-accent'}>{item.icon}</span>
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2 text-sm font-medium text-ink">
                    {item.title}
                    {item.urgent && <Badge tone="danger">عاجل</Badge>}
                  </span>
                  <span className="block text-xs text-ink-3">{item.hint}</span>
                </span>
                <span className="tabular text-xl font-semibold text-ink">{item.count}</span>
                <IconChevron className="size-4 rotate-180 text-ink-3" />
              </Link>
            </li>
          ))}
        </ul>
      )}

      {failed.length > 0 && (
        <p role="alert" className="mt-3 text-sm text-danger">
          تعذّر تحميل: {failed.join('، ')}. قد تكون هناك بنود غير ظاهرة؛ حدّث الصفحة بعد قليل.
        </p>
      )}
    </section>
  );
}
